import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';

function OfferingIncludedProducts({ product }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [processedData, setProcessedData] = useState({ features: [], standaloneOptions: [] });

  useEffect(() => {
    if (product && product.related_data) {
      const features = product.related_data.features || [];
      const options = product.related_data.productOptions || [];

      const featureMap = new Map(features.map((f) => [f.sfId || f.id, { ...f, options: [] }]));
      const standaloneOptions = [];

      options.forEach((option) => {
        const featureId = option.fields?.SBQQ__Feature__c;
        if (featureId && featureMap.has(featureId)) {
          featureMap.get(featureId).options.push(option);
        } else {
          standaloneOptions.push(option);
        }
      });

      setProcessedData({
        features: Array.from(featureMap.values()),
        standaloneOptions,
      });
    } else {
      // Ensure state is cleared if product has no related_data
      setProcessedData({ features: [], standaloneOptions: [] });
    }
  }, [product]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  const renderOptionsList = (options) => {
    if (!options || options.length === 0) {
      return (
        <ArgonBox sx={{ pl: 4, py: 2 }}>
          <ArgonTypography variant="body2">No options available for this feature.</ArgonTypography>
        </ArgonBox>
      );
    }
    return (
      <ArgonBox sx={{ pl: 4, py: 1, borderTop: `1px solid ${grey[200]}` }}>
        {options.map((option) => (
          <ArgonBox key={option.sfId || option.id} py={0.75}>
            <ArgonTypography variant="body2" color="secondary" fontWeight="regular">
              {option.product?.name || 'N/A'}
            </ArgonTypography>
          </ArgonBox>
        ))}
      </ArgonBox>
    );
  };

  const renderFeatureCard = (feature) => {
    const featureId = feature.sfId || feature.id;
    const isExpanded = expandedRow === featureId;
    return (
      <Card key={featureId} sx={{ mb: 1 }} variant="outlined">
        <ArgonBox
          p={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => handleRowClick(featureId)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
            {feature.name}
          </ArgonTypography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(featureId);
            }}
          >
            <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        </ArgonBox>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {renderOptionsList(feature.options)}
        </Collapse>
      </Card>
    );
  };

  const hasContent = processedData.features.length > 0 || processedData.standaloneOptions.length > 0;

  return (
    <Card>
      <ArgonBox p={3}>
        {!hasContent ? (
          <ArgonTypography variant="body1" color="textSecondary">
            No features or options are configured for this offering.
          </ArgonTypography>
        ) : (
          <>
            {processedData.features.length > 0 && (
              <ArgonBox>
                <ArgonTypography variant="h6" mb={2}>
                  Features
                </ArgonTypography>
                {processedData.features.map((feature) => renderFeatureCard(feature))}
              </ArgonBox>
            )}
            {processedData.standaloneOptions.length > 0 && (
              <ArgonBox mt={4}>
                <ArgonTypography variant="h6" mb={2}>
                  Standalone Options
                </ArgonTypography>
                {processedData.standaloneOptions.map((option) => (
                  <Card key={option.sfId || option.id} sx={{ mb: 1, p: 1.5 }} variant="outlined">
                    <ArgonTypography variant="body2" color="secondary" fontWeight="regular">
                      {option.product?.name || 'N/A'}
                    </ArgonTypography>
                  </Card>
                ))}
              </ArgonBox>
            )}
          </>
        )}
      </ArgonBox>
    </Card>
  );
}

export default OfferingIncludedProducts;
